import { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { FlightsClassTypes } from 'constants/flight'
import { MALDIVES_BUNDLE_DEFAULT_GUESTS } from 'constants/charterFlight'
import { buildCharterFlightSearchKey, shouldShowKidsFlyFreeLabel } from 'lib/charterFlights/kidsFlyFreeUtils'
import { selectKidsFlyFreeDates } from 'selectors/charterFlights/kidsFlyFreeSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import GeoContext from 'contexts/geoContext'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

const DEFAULT_OCCUPANCIES: Array<App.Occupants> = [{ adults: MALDIVES_BUNDLE_DEFAULT_GUESTS }]
const EMPTY_SET = new Set<string>()

/**
 * Unified hook for all Kids Fly Free logic.
 *
 * Reads already-fetched flight data from Redux for the given fareClass,
 * derives kids-fly-free departure dates via a memoized selector,
 * and combines feature flag + campaign tags + API date matching
 * into a single API that all consumers share.
 *
 * Does NOT trigger API fetches — callers must ensure flight data
 * is already in Redux before this hook is used.
 */
export default function useKidsFlyFree(params: {
  charterBundleRoute?: App.CharterBundleRoute
  occupancies?: Array<App.Occupants>
  fareClass?: FlightsClassTypes
}) {
  const {
    charterBundleRoute,
    occupancies = DEFAULT_OCCUPANCIES,
    fareClass = FlightsClassTypes.ECONOMY,
  } = params
  const enabled = useFeatureFlag(OptimizelyFeatureFlags.childrenFlyFree)
  const { currentCurrency } = useContext(GeoContext)

  const occupancyKey = JSON.stringify(occupancies)
  const searchKey = useMemo(
    () => charterBundleRoute
      ? buildCharterFlightSearchKey(charterBundleRoute, occupancies, fareClass, currentCurrency)
      : '',
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- occupancyKey stabilises the array reference
    [charterBundleRoute, occupancyKey, fareClass, currentCurrency],
  )

  const selector = useCallback(
    (state: App.State) => searchKey ? selectKidsFlyFreeDates(state, searchKey) : EMPTY_SET,
    [searchKey],
  )
  const kidsFlyFreeDates = useAppSelector(selector)

  const isKidsFlyFreeDate = useCallback(
    (date: string) => enabled && kidsFlyFreeDates.has(date),
    [enabled, kidsFlyFreeDates],
  )

  const hasKidsFlyFreeDateInMonth = useCallback(
    (year: number, month: number) => {
      if (!enabled) return false
      for (const d of kidsFlyFreeDates) {
        const parsed = moment(d)
        if (parsed.year() === year && parsed.month() === month) return true
      }
      return false
    },
    [enabled, kidsFlyFreeDates],
  )

  const shouldShowKidsFlyFreeForOffer = useCallback(
    (offer: { campaignTags?: Array<string> }, checkIn?: string) => {
      if (!enabled) return false
      if (!shouldShowKidsFlyFreeLabel(offer)) return false
      if (kidsFlyFreeDates.size === 0) return false
      if (!checkIn) return true
      const departDate = moment(checkIn).subtract(1, 'day').format(ISO_DATE_FORMAT)
      return kidsFlyFreeDates.has(departDate)
    },
    [enabled, kidsFlyFreeDates],
  )

  return {
    enabled,
    kidsFlyFreeDates,
    isKidsFlyFreeDate,
    hasKidsFlyFreeDateInMonth,
    shouldShowKidsFlyFreeForOffer,
  }
}
